import React, { useState, useEffect } from 'react';
import {
  Spin, Button, Card, Tag, message, Tooltip, Input, Modal, Space, Table
} from 'antd';
import {
  EditOutlined,
  StopOutlined,
  CheckOutlined
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import Fuse from 'fuse.js';
import _ from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux';
import * as Service from '../core/Service';
import { ActionCreators } from '../redux/actions';
import NavAndSideFrame from '../components/NavAndSideFrame';
import VehicleForm from '../components/VehicleForm';
import { useTranslation } from 'react-i18next';
import { displayIsActive, displayVehicleType } from '../components/table/DisplayMethod';

const title = `車輛列表`;
const selectedKey = `vehicle_list`;
const tableIdName = `vehicle_id`;
const filterableField = [
  'call_sign',
  'vehicle_license_no',
];
const defaultFormData = {
  vehicle_license_no: undefined,
  call_sign: undefined,
  vehicle_type_id: undefined,
  seat_number: undefined,
  is_active: undefined,
}

const VehicleList = (props) => {
  const {t} = useTranslation();

  const [dataList, setDataList] = useState([]);
  const [unChangeData, setUnChangeData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const [isChangingIsActive, setChangingIsActive] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = async () => {
    let endpoint = `/api/vehicle/list`;
    let list = [];
    setLoading(true);
    try {
      let data = await Service.call(`GET`, endpoint);
      list = _.orderBy(data, [`ctime`], [`desc`]);
      if (data.errorMessage) list = [];
    } catch (error) {
      console.log(error);
    } finally {
      setDataList(list);
      setUnChangeData(list);
      setLoading(false);
    }
  }

  const setTableHeader = () => {
    let columns = [
      {
        title: '操作',
        dataIndex: tableIdName,
        render: (value, record) => {
          return (
            <span>
              <Tooltip title={t('common.edit')}>
                <Button
                  shape="circle"
                  style={{ marginRight: 8, color: '#000000' }}
                  icon={<EditOutlined />}
                  onClick={() => {
                    setSelectedRecord(record);
                    setModalVisible(true);
                  }}
                />
              </Tooltip>
              <Tooltip title={record.is_active ? t('vehicle.disable_vehicle') : t('vehicle.enable_vehicle')}>
                <Button
                  shape="circle"
                  style={{ color: record.is_active ? 'red' : 'green' }}
                  icon={record.is_active ? (<StopOutlined />) : (<CheckOutlined />)}
                  onClick={async () => {
                    if (isChangingIsActive) return; // avoid multiple attempts

                    setChangingIsActive(true);
                    let { vehicle_id, is_active } = record;
                    is_active = !is_active;

                    let result = await Service.call('put', `/api/vehicle`, { vehicle_id, is_active });
                    setChangingIsActive(false);

                    if (_.isEmpty(result) || result.status < 1) {
                      return message.error(is_active ? t('vehicle.fail_enable_vehicle') : t('vehicle.fail_disable_vehicle'));
                    }
                    await getAllData();
                    return message.success(is_active ? t('vehicle.vehicle_enabled') : t('vehicle.vehicle_disabled'));
                  }}
                />
              </Tooltip>
            </span>
          )
        }
      },
      { title: t('vehicle.vehicle_license_no'), dataIndex: tableIdName },
      {
        title: t('common.is_active'),
        dataIndex: 'is_active',
        render: (value) => {
          return displayIsActive(value, t);
        },
        ellipsis: true
      },
      { title: t('vehicle.vehicle_license_no'), dataIndex: `vehicle_license_no` },
      { title: t('vehicle.call_sign'), dataIndex: `call_sign`},
      { title: t('vehicle.vehicle_type'),
        dataIndex: `vehicle_type_id` ,
        render: (value) => {
          return displayVehicleType(value);
        }
      },
      { title: t('vehicle.seat_number'), dataIndex: `seat_number` },
      {
        title: t('common.ctime'),
        dataIndex: 'ctime',
        render: (value) => {
          return displayMoment(value);
        },
        ellipsis: true,
      },
      {
        title: t('common.utime'),
        dataIndex: 'utime',
        render: (value) => {
          return displayMoment(value);
        },
        ellipsis: true,
      },

    ];
    return columns;
  }

  const displayMoment = (unixTime, outputFormat = 'YYYY/MM/DD HH:mm') => {
    if (unixTime !== 0) {
      let displayTime = moment.unix(unixTime).format(outputFormat);
      return displayTime;
    }
    return '-';
  }

  const getDataBySearchKeyword = (keywords) => {
    let dataList = [];
    try {
      const options = {
        includeScore: true,
        findAllMatches: true,
        threshold: 0.15,
        keys: filterableField
      }
      const fuse = new Fuse(unChangeData, options)
      const result = fuse.search(keywords);
      console.log("RESULT", result);
      dataList = _.map(result, 'item');
      dataList = _.orderBy(dataList, [`ctime`], [`desc`]);
    } catch (error) {
      console.error('error >>> ', error);
    } finally {
      setDataList(dataList);
    }
  }

  let timeout;
  const debounce = (func, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(func, delay);
  };

  const onChange = (keyword) => {
    debounce(() => {
      getDataBySearchKeyword(keyword);
      if (_.isEmpty(keyword)) setDataList(unChangeData);
    }, 400);
  };

  return (
    <NavAndSideFrame {...props} title={title} selectedKey={selectedKey}>
      <Card>
        <Button
          icon={<EditOutlined />}
          style={{ marginRight: 10 }}
          type="primary"
          onClick={() => {
            setSelectedRecord(defaultFormData);
            setModalVisible(true);
          }}
        >
          新增
        </Button>

        <Space direction="horizontal">
          <>以文字搜尋:</>
          <Input
            placeholder="請輸入文字"
            onChange={(values) => {
              onChange(values.target.value);
            }}
          />
        </Space>
      </Card>

      <Card>
        <Spin spinning={loading}>
          <Table
            pagination={{
              defaultPageSize: 25,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200, 500]
            }}

            scroll={{ x: `max-content` }}
            dataSource={dataList}
            columns={setTableHeader()}
            rowKey={tableIdName}
          />
        </Spin>
      </Card>
      <Modal
        title={t('vehicle.vehicle')}
        visible={modalVisible}
        footer={null}
        style={{ maxWidth: 800 }}
        width="90%"
        onCancel={() => { setModalVisible(false) }}
        destroyOnClose
      >
        <VehicleForm
          selectedRecord={selectedRecord}
          openModal={(value) => {
            getAllData();
            setModalVisible(value);
          }}
        />
      </Modal>

    </NavAndSideFrame>
  );
}

// export default VehicleList;

function mapStateToProps(state) {
  return {
    app: state.app
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VehicleList);